import React, { useState } from 'react';
import { purchaseMessages } from '../../utils/velvypalApi';
import { FEATURE_PRICING } from '../../config/featurePricing';
import { logger } from '../../utils/logger';
import './BuyMessagesModal.css';

/**
 * Modal for purchasing messages with gems
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Handler to close modal
 * @param {Object} props.user - Firebase user object
 * @param {number} props.currentGems - User's current gem balance
 * @param {number} props.purchasedMessages - Current purchased messages
 * @param {Function} props.onPurchaseSuccess - Called after successful purchase
 * @param {Function} props.onBuyGems - Handler to open gems purchase modal
 */
export default function BuyMessagesModal({
  isOpen,
  onClose,
  user,
  currentGems = 0,
  purchasedMessages = 0,
  onPurchaseSuccess,
  onBuyGems,
}) {
  const [selectedPack, setSelectedPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const messagePacks = FEATURE_PRICING.velvypal.messagePacks;

  const handlePurchase = async () => {
    if (!user || !selectedPack) return;

    setLoading(true);
    setError(null);

    try {
      await purchaseMessages(user, selectedPack.messages, selectedPack.gems);
      onPurchaseSuccess?.();
      onClose();
    } catch (err) {
      logger.error('Purchase error:', err);
      setError(err.message || 'Failed to purchase messages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const handleBuyGems = () => {
    onClose();
    onBuyGems?.();
  };

  if (!isOpen) return null;

  return (
    <div className="buy-messages-overlay" onClick={handleOverlayClick}>
      <div className="buy-messages-modal">
        <button
          className="buy-messages-close"
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <div className="buy-messages-header">
          <h2>Buy Messages</h2>
          <p className="buy-messages-status">
            Current purchased messages: <strong>{purchasedMessages}</strong>
          </p>
          <p className="buy-messages-gems">
            Your gems: <strong>{currentGems}</strong>
          </p>
        </div>

        {error && (
          <div className="buy-messages-error">
            {error}
          </div>
        )}

        <div className="buy-messages-section">
          <p className="buy-messages-section-title">Choose a message pack:</p>
          <div className="buy-messages-packs">
            {messagePacks.map((pack, index) => {
              const canAfford = currentGems >= pack.gems;
              const isSelected = selectedPack?.messages === pack.messages;
              const perMessage = Math.round(pack.gems / pack.messages);
              const isBestValue = index === messagePacks.length - 1;

              return (
                <button
                  key={pack.messages}
                  className={`buy-messages-pack ${isSelected ? 'selected' : ''} ${!canAfford ? 'disabled' : ''}`}
                  onClick={() => canAfford && setSelectedPack(pack)}
                  disabled={loading || !canAfford}
                >
                  {isBestValue && <span className="buy-messages-pack-badge">Best Value</span>}
                  <span className="buy-messages-pack-amount">+{pack.messages}</span>
                  <span className="buy-messages-pack-label">message{pack.messages !== 1 ? 's' : ''}</span>
                  <span className="buy-messages-pack-cost">{pack.gems} gems</span>
                  <span className="buy-messages-pack-unit">
                    ~{perMessage} gems each
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="buy-messages-tip">
          <span>&#128161;</span>
          <span>Purchased messages never expire and are used after daily free messages</span>
        </div>

        <div className="buy-messages-actions">
          {selectedPack ? (
            <button
              className="buy-messages-action-primary"
              onClick={handlePurchase}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="buy-messages-spinner"></span>
                  Processing...
                </>
              ) : (
                `Purchase ${selectedPack.messages} message${selectedPack.messages !== 1 ? 's' : ''}`
              )}
            </button>
          ) : (
            <button
              className="buy-messages-action-secondary"
              onClick={handleBuyGems}
              disabled={loading}
            >
              Buy More Gems
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
